// Lots of micro-components in this file.  May want to give them display names via named functions in the future, but disable errors for now
/* eslint-disable react/display-name */

import { h } from 'preact';
import { Text } from 'preact-i18n';
import { formatNumber, intlDateNode, gle, generatePriceFormatter, isCurrency } from '../../lib/util';
import { FinanceSpinner } from '../glyphs';
import { FootnoteReference } from '../footnotes';
import { getGoodPrevClose } from '../../lib/util-stock-price';
import style from './style';

export const INTL_NS = 'stock-details';

const StockDetails = ({ fields, quote, ...props }) => {
	if (!quote || (typeof quote === 'object' && !Object.keys(quote).length)) {
		return (
			<div class={style['stock-details']}>
				<FinanceSpinner />
			</div>
		);
	}
	quote.formatter = generatePriceFormatter(quote, { Default: <Text id={`${INTL_NS}.na`} /> });

	return (
		<div class={style['stock-details']}>
			<table class={style['stock-details-table']}>
				{props.header && <StockDetailHeader title={props.header} />}
				<tbody>
					{fields.map(field => h(FieldComponents[field], { field, fields, quote, ...props }))}
				</tbody>
			</table>
		</div>
	);
};
export default StockDetails;

const StockDetailHeader = ({ title }) => (
	<thead>
		<th colspan="2">{title}</th>
	</thead>
);

export const FieldComponents = {
	volume: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.volume`} /></th>
			<td>{formatNumber(quote.Volume, { Decimals: 0, Comma: true, Default: '0' })}</td>
		</tr>
	),

	averageVolume: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.averageVolume`} /></th>
			<td>{formatNumber(quote.AvgVolume3M, { Decimals: 0, Comma: true, Default: '0' })}</td>
		</tr>
	),

	open: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.open`} /></th>
			<td>{quote.formatter(quote.Open)}</td>
		</tr>
	),

	profitOverEarnings: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.profitOverEarnings`} /></th>
			<td>
				{formatNumber(quote.Price / quote.EPS, {
					Comma: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	marketCap: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.marketCap`} /></th>
			<td>
				{' '}
				{formatNumber(quote.SharesOutstanding * quote.Price, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
				{' '}
			</td>
		</tr>
	),

	marketCapSecurityStats: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.marketCapSecurityStats`} />
				{' '}
				<FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td>
				{' '}
				{formatNumber(quote.MarketCap, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
				{' '}
			</td>
		</tr>
	),

	enterpriseValue: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.enterpriseValue`} /></th>
			<td>
				{formatNumber(quote.EnterpriseValue, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	trailingPe: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.trailingPe`} /> <FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td>
				{formatNumber(quote.TrailingPE, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	priceToSale: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.priceToSale`} /></th>
			<td>
				{formatNumber(quote.PriceToSales, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	priceToBook: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.priceToBook`} /></th>
			<td>
				{formatNumber(quote.PriceToBook, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	enterpriseValueRevenue: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.enterpriseValueRevenue`} />
				{' '}
				<FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td>
				{formatNumber(quote.EnterpriseValueToRevenue, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	enterpriseValueEbitda: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.enterpriseValueEbitda`} />
				{' '}
				<FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td>
				{formatNumber(quote.EnterpriseValueToEBITDA, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	fiscalYearEnds: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.fiscalYearEnds`} />
				{' '}
				<FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td>{intlDateNode(quote.FiscalYearEnd)}</td>
		</tr>
	),

	mostRecentQuarter: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.mostRecentQuarter`} />
				{' '}
				<FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td>{intlDateNode(quote.MostRecentQuarter)}</td>
		</tr>
	),

	profitMargin: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.profitMargin`} /></th>
			<td>
				{formatNumber(quote.ProfitMargin, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Suffix: '%',
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	operatingMargin: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.operatingMargin`} /></th>
			<td>
				{formatNumber(quote.OperatingMargin, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Suffix: '%',
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	returnOnAssets: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.returnOnAssets`} />
				{' '}
				<FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td>
				{formatNumber(quote.ReturnOnAssets, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Suffix: '%',
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	returnOnEquity: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.returnOnEquity`} />
				<FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td>
				{formatNumber(quote.ReturnOnEquity, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Suffix: '%',
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	revenue: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.revenue`} /></th>
			<td>
				{formatNumber(quote.Revenue, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	revenuePerShare: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.revenuePerShare`} />
				{' '}
				<FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td>
				{formatNumber(quote.RevenuePerShare, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	quarterlyRevenueGrowth: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.quarterlyRevenueGrowth`} />
				{' '}
				<FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td
				class={`${style['stock-details-table']} ${gle(quote.QuarterlyRevenueGrowth, 0, style.positive, style.negative, '')}`}
			>
				{formatNumber(quote.QuarterlyRevenueGrowth, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Suffix: '%',
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	grossProfit: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.grossProfit`} /></th>
			<td>
				{formatNumber(quote.GrossProfit, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	ebitda: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.ebitda`} /></th>
			<td>
				{formatNumber(quote.EBITDA, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	dilutedEps: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.dilutedEps`} /></th>
			<td>
				{formatNumber(quote.DilutedEPS, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	quarterlyEarningsGross: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.quarterlyEarningsGross`} />
				{' '}
				<FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td
				class={`${style['stock-details-table']} ${gle(quote.QuarterlyEarningsGrowth, 0, style.positive, style.negative, '')}`}
			>
				{formatNumber(quote.QuarterlyEarningsGrowth, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	totalCash: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.totalCash`} /></th>
			<td>
				{formatNumber(quote.TotalCash, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	totalCashPerShare: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.totalCashPerShare`} />
				{' '}
				<FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td>
				{formatNumber(quote.TotalCashPerShare, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	totalDebt: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.totalDebt`} /></th>
			<td>
				{formatNumber(quote.TotalDebt, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	totalDebtEquity: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.totalDebtEquity`} />
				{' '}
				<FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td>
				{formatNumber(quote.TotalDebtToEquity, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	currentRatio: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.currentRatio`} />
				{' '}
				<FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td>
				{formatNumber(quote.CurrentRatio, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	bookValuePerShare: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.bookValuePerShare`} />
				{' '}
				<FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td>
				{formatNumber(quote.BookValuePerShare, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	operatingCashFlow: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.operatingCashFlow`} /></th>
			<td>
				{formatNumber(quote.OperatingCashFlow, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	leveredFreeCashFlow: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.leveredFreeCashFlow`} /></th>
			<td>
				{formatNumber(quote.FreeCashFlow, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	oneYearHigh: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.oneYearHigh`} /></th>
			<td>
				{formatNumber(quote['1YearHigh'], {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	oneYearChange: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.oneYearChange`} /></th>
			<td>
				{formatNumber(quote['1YearChange'], {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Suffix: '%',
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	oneYearLow: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.oneYearLow`} /></th>
			<td>
				{formatNumber(quote['1YearLow'], {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	oneWeekAvgVolume: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.oneWeekAvgVolume`} /></th>
			<td>
				{formatNumber(quote.AverageVolume1Week, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	threeMonthAvgVolume: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.threeMonthAvgVolume`} /></th>
			<td>
				{formatNumber(quote.AverageVolume3Month, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	sharesOutstanding: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.sharesOutstanding`} /></th>
			<td>
				{formatNumber(quote.SharesOutstanding, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	vsDowWeek: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.vsDowWeek`} /></th>
			<td
				class={`${style['stock-details-table']} ${gle(quote['1WeekPerformanceVsDow'], 0, style.positive, style.negative, '')}`}
			>
				{formatNumber(quote['1WeekPerformanceVsDow'], {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Suffix: '%',
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	vsSPWeek: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.vsSPWeek`} /></th>
			<td
				class={`${style['stock-details-table']} ${gle(quote['1WeekPerformanceVsSP500'], 0, style.positive, style.negative, '')}`}
			>
				{formatNumber(quote['1WeekPerformanceVsSP500'], {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Suffix: '%',
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	vsNasdaqWeek: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.vsNasdaqWeek`} /></th>
			<td
				class={`${style['stock-details-table']} ${gle(quote['1WeekPerformanceVsNasdaqComposite'], 0, style.positive, style.negative, '')}`}
			>
				{formatNumber(quote['1WeekPerformanceVsNasdaqComposite'], {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Suffix: '%',
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	vsDowYear: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.vsDowYear`} /></th>
			<td
				class={`${style['stock-details-table']} ${gle(quote['1YearPerformanceVsDow'], 0, style.positive, style.negative, '')}`}
			>
				{formatNumber(quote['1YearPerformanceVsDow'], {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Suffix: '%',
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	vsSPYear: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.vsSPYear`} /></th>
			<td
				class={`${style['stock-details-table']} ${gle(quote['1YearPerformanceVsSP500'], 0, style.positive, style.negative, '')}`}
			>
				{formatNumber(quote['1YearPerformanceVsSP500'], {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Suffix: '%',
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	vsNasdaqYear: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.vsNasdaqYear`} /></th>
			<td
				class={`${style['stock-details-table']} ${gle(quote['1YearPerformanceVsNasdaqComposite'], 0, style.positive, style.negative, '')}`}
			>
				{formatNumber(quote['1YearPerformanceVsNasdaqComposite'], {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Suffix: '%',
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	bid: ({ quote }) => (
		quote.Bid > 0
			? <tr class={style.index}>
				<th><Text id={`${INTL_NS}.bid`} /></th>
				<td>
					{quote.formatter(quote.Bid)}
					{' '}
					{ !isCurrency(quote) && ['* ', quote.formatter(quote.BidSize, { Decimals: 0 })] }
				</td>
			</tr>
			: null
	),

	trailingDividendRate: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.trailingDividendRate`} /></th>
			<td>
				{formatNumber(quote.TrailingAnnualDividendRate, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	trailingDividendYield: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.trailingDividendYield`} /></th>
			<td>
				{formatNumber(quote.TrailingAnnualDividendYield, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Suffix: '%',
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	payoutRatio: ({ quote, field, footnotes }) => (
		<tr class={style.index}>
			<th>
				<Text id={`${INTL_NS}.payoutRatio`} />
				{' '}
				<FootnoteReference id={field} footnotes={footnotes} />
			</th>
			<td>
				{formatNumber(quote.PayoutRatio, {
					Comma: true,
					Compact: true,
					Decimals: 2,
					Suffix: '%',
					Default: <Text id={`${INTL_NS}.na`} />
				})}
			</td>
		</tr>
	),

	ask: ({ quote }) => (
		quote.Ask > 0
			?
			<tr class={style.index}>
				<th><Text id={`${INTL_NS}.ask`} /></th>
				<td>
					{quote.formatter(quote.Ask)}
					{' '}
					{ !isCurrency(quote) && ['* ', quote.formatter(quote.AskSize, { Decimals: 0 })] }
				</td>
			</tr>
			:
			null
	),

	prevClose: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.prevClose`} /></th>
			<td>{quote.formatter(getGoodPrevClose(quote))}</td>
		</tr>
	),

	dayRange: ({ quote }) => (
		<tr class={style.index}>
			<th><Text id={`${INTL_NS}.dayRange`} /></th>
			<td>{quote.formatter(quote.Low)} - {quote.formatter(quote.High)}</td>
		</tr>
	),

	'52WeekRange': ({ quote }) => (
		<tr class={style['52Range']}>
			<th><Text id={`${INTL_NS}.52WeekRange`} /></th>
			<td>{quote.formatter(quote.Low52)} - {quote.formatter(quote.High52)}</td>
		</tr>
	),

	dividend: ({ quote }) => (
		quote.AnnualDividend > 0
			?
			<tr class={style.dividend}>
				<th><Text id={`${INTL_NS}.dividend`} /></th>
				<td>
					{formatNumber(quote.AnnualDividend, {
						Comma: true,
						Decimals: 2,
						Default: <Text id={`${INTL_NS}.na`} />
					})}
				</td>
			</tr>
			:
			null
	),

	yield: ({ quote }) => (
		(quote.AnnualDividend > 0 && quote.Price > 0)
			?
			<tr class={style.yield}>
				<th><Text id={`${INTL_NS}.yield`} /></th>
				<td>{formatNumber(quote.AnnualDividend * 100 / quote.Price, {
					Decimals: 2,
					Default: <Text id={`${INTL_NS}.na`} />,
					Suffix: '%'
				})}
				</td>
			</tr>
			:
			null
	)
};
